import React from "react";
import { useIntl } from "react-intl";
import { Alert, AlertIcon } from "@chakra-ui/react";
import EmptyState from "components/EmptyState";

/**
 * useTOSStatusErrorCopy
 * @returns {{title: string, description: string, message: string}}
 * @description The one copy for "we could not establish ToS status", shared because the `message`
 * a host sends the integrator through `onError` has to say the same thing as the screen the user is
 * looking at - and because a ToS failure must not borrow the invoice's copy. `title`/`description`
 * are the terminal rendering, `message` the one-sentence form the inline alert and `onError` use.
 */
export const useTOSStatusErrorCopy = () => {
  const { formatMessage } = useIntl();
  return {
    title: formatMessage({
      defaultMessage: "Something went wrong",
      description: "Status title",
    }),
    description: formatMessage({
      defaultMessage:
        "We were unable to verify your Terms of Service status. Please try again later.",
      description: "Status message",
    }),
    message: formatMessage({
      defaultMessage: "Unable to verify Terms of Service status. Please try again later.",
      description: "Alert when the ToS status fetch fails",
    }),
  };
};

/**
 * useTOSSignErrorCopy
 * @returns {{title: string, message: string}}
 * @description The one sentence for "we could not record your acceptance". Shared because the
 * inline consent block and Checkout's toast both show it, and because the alternative is rendering
 * `signError.message` - which `TiliaAPIError` takes from `response.statusText`, so it would put an
 * untranslated "Internal Server Error" under the checkbox in every locale. The error itself goes
 * to `logError`/`onError`, not to the screen.
 */
export const useTOSSignErrorCopy = () => {
  const { formatMessage } = useIntl();
  return {
    title: formatMessage({
      defaultMessage: "Unable to record your acceptance",
      description: "Popup title",
    }),
    message: formatMessage({
      defaultMessage:
        "We could not record your Terms of Service acceptance. Please try again later.",
      description: "Popup description",
    }),
  };
};

/**
 * TOSStatusError
 * @description The inline rendering of "we could not establish ToS status", for the one surface
 * where a status failure is genuinely recoverable without a remount: <AddPaymentMethodContent />
 * drives its CTAs through `useTOSGatedAction`, whose `ensureNotBlocked` refetches the status on
 * every click, so the alert sits next to controls that do re-decide. Where the only effect of a
 * failure is a disabled button, hosts render <TOSStatusErrorEmptyState /> instead - an alert with
 * nothing to act on is a dead end.
 */
export const TOSStatusError = () => {
  const { message } = useTOSStatusErrorCopy();
  return (
    <Alert status="error" variant="left-accent" data-testid="tos-status-error">
      <AlertIcon />
      {message}
    </Alert>
  );
};

/**
 * TOSStatusErrorEmptyState
 * @description The terminal rendering, for hosts that gate a disabled CTA on the status. We fail
 * closed, so an unresolved status blocks the already-signed majority just as hard as anyone else -
 * which leaves no middle ground to hold: the surface cannot be used, and the recovery that exists
 * in production is the integrator re-opening the widget on `onError`/`FLOW_STATE.ERROR`. Same
 * treatment a failed settings fetch gets (see <SettingsProvider />).
 *
 * Title and description rather than an `error` prop: EmptyState renders `error.message`, which
 * TiliaAPIError takes from `response.statusText`, so a 500 would put an untranslated "Internal
 * Server Error" on screen in every locale. Matches <InvalidLinkEmptyState />.
 */
export const TOSStatusErrorEmptyState = () => {
  const { title, description } = useTOSStatusErrorCopy();
  return <EmptyState title={title} description={description} />;
};

export default TOSStatusError;
